/* Copyright (c) 2020-present CloudIO Inc */

import { StateXProvider } from '@cloudio-saas/statex';
import { ExpressionBuilder } from 'expression/builder/ExpressionBuilder';
import { OnInitCallback } from 'init/types';
import PublicLog from 'platform/console/PublicLog';
import ErrorBoundary from 'platform/ErrorBoundary';
import ImportData from 'platform/ImportData';
import { Notification } from 'platform/Notification';
import { usePlatform } from 'platform/PlatformHooks';
import { EMPTY_PAGES, PlatformProvider } from 'platform/PlatformProvider';
import React, { Suspense } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import Loading from 'root/SimpleLoading';
import { initialState } from 'store/Store';
import Tooltip from 'utils/Tooltip';
import HowTo from '../howto/HowTo';
import HowToHome from '../howto/HowToHome';
import ScreenCapture from '../howto/screenshot';
import ThemeWrapper from '../theme/ThemeWrapper';
import NotFound from './NotFound';
import SecureRoute from './SecureRoute';
import AsyncExport from 'platform/AsyncExport/AsyncExport';

// const Tree = React.lazy(() => import(/* webpackChunkName: "tree" */ './Tree'));

// const Play = React.lazy(() => import(/* webpackChunkName: "play" */ './Play'));

const SignIn = React.lazy(
  () => import(/* webpackChunkName: "si2" */ './SignIn'),
);

const ResetPassword = React.lazy(
  () => import(/* webpackChunkName: "rp" */ './ResetPassword'),
);

const ContactAdmin = React.lazy(
  () => import(/* webpackChunkName: "ca" */ './ContactAdmin'),
);

const APIPlayGround = React.lazy(
  () => import(/* webpackChunkName: "api" */ '../api/APIPlayGround'),
);

const MainRoute = React.lazy(
  () => import(/* webpackChunkName: "mr" */ './MainRoute'),
);

const Help = React.lazy(() => import(/* webpackChunkName: "help" */ './Help'));

function MainWithStateX({
  onInit,
}: {
  onInit: OnInitCallback;
}): React.ReactElement {
  const platform = usePlatform();
  return (
    <ThemeWrapper>
      <ScreenCapture platform={platform}>
        <Suspense
          // @ts-ignore unstable_avoidThisFallback
          unstable_avoidThisFallback
          fallback={<Loading />}
        >
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/contact-admin" element={<ContactAdmin />} />
            <Route path="/api" element={<APIPlayGround />} />
            <Route path="/help" element={<Help onInit={onInit} />} />
            <Route
              path="/logs/:key"
              element={<PublicLog platform={platform} />}
            />
            <Route
              path="/how-to/:appUid"
              element={<HowToHome platform={platform} />}
            />
            <Route
              path="/how-to/:appUid/:howToUid"
              element={<HowTo platform={platform} />}
            />
            <Route
              path="/:appUid?/:pageUid?/:id?"
              element={
                <SecureRoute>
                  <ErrorBoundary platform={platform}>
                    <MainRoute onInit={onInit} />
                  </ErrorBoundary>
                </SecureRoute>
              }
            />
            <Route path="*" element={<NotFound reason="Unexpected URL!" />} />
          </Routes>
        </Suspense>
        <Tooltip />
        <Notification platform={platform} />
        <ExpressionBuilder platform={platform} />
        <ImportData platform={platform} />
        <AsyncExport platform={platform} />
      </ScreenCapture>
    </ThemeWrapper>
  );
}

export default function Main({
  onInit,
}: {
  onInit: OnInitCallback;
}): React.ReactElement {
  return (
    <HashRouter>
      <StateXProvider
        initialState={initialState}
        debug={false}
        // debugId="cell-2-0"
        debugPath={[
          [
            'transient',
            'designer',
            'visibleChildren',
            'xcelerateiot',
            'setup-areas',
            'form',
          ],
          [
            'root',
            'dataStore',
            'xcelerateiot',
            'setup-areas',
            'IotAreas',
            'iotAreasEditAlias',
          ],
        ]}
        debugPathExactMatch={false}
      >
        <PlatformProvider
          appUid="cloudio"
          pageId="$"
          roleUid="$"
          pages={EMPTY_PAGES}
        >
          <MainWithStateX onInit={onInit} />
        </PlatformProvider>
      </StateXProvider>
    </HashRouter>
  );
}
